// Vetrina pubblica — dettaglio immobile "Case in vendita" + form contatti (lead) verso CRM admin.
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { mediaUrl, formatApiError } from '@/lib/api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { realEstateService } from '@/services/realEstate.service';
import { PublicLayout } from '@/components/public/PublicLayout';
import { ExperiencesCrossSell } from '@/components/public/ExperiencesCrossSell';
import { Seo } from '@/components/Seo';
import { Skeleton } from '@/components/ui/skeleton';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, MapPin, Maximize, BedDouble, Bath, Zap, Building2, ArrowUpDown, ImageIcon, Send, Loader2, CheckCircle2, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const euro = (n) => (n == null ? 'Prezzo su richiesta' : `€${Number(n).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`);
const stripHtml = (h) => (h || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

function Spec({ icon: Icon, label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
      <Icon className="h-4 w-4 text-primary" />
      <div className="leading-tight"><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm font-medium">{value}</p></div>
    </div>
  );
}

export default function CaseInVenditaDetail() {
  const { id } = useParams();
  const { data: l, isLoading, isError } = useApiQuery(['public', 'real-estate', id], () => realEstateService.publicDetail(id));
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error('Nome ed email sono obbligatori'); return; }
    setSending(true);
    try {
      const res = await realEstateService.submitLead(id, form);
      setSent(true);
      toast.success(res.message || 'Richiesta inviata');
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Invio non riuscito'); }
    finally { setSending(false); }
  };

  if (isLoading) {
    return <PublicLayout><div className="sila-section py-12"><Skeleton className="h-[420px] w-full rounded-2xl" /></div></PublicLayout>;
  }
  if (isError || !l) {
    return (
      <PublicLayout>
        <div className="sila-section py-20 text-center">
          <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-3 font-medium">Immobile non disponibile</p>
          <Link to="/case-in-vendita" className="mt-4 inline-flex items-center gap-1 text-primary hover:underline"><ArrowLeft className="h-4 w-4" />Torna alle case in vendita</Link>
        </div>
      </PublicLayout>
    );
  }

  const photos = l.photos || [];
  const cover = photos[active]?.url;
  const address = [l.street, l.building, l.city, l.province].filter(Boolean).join(', ');
  const mapQuery = (l.lat != null && l.lng != null) ? `${l.lat},${l.lng}` : (address ? encodeURIComponent(address) : '');
  const mapHref = mapQuery ? `https://www.google.com/maps/search/?api=1&query=${mapQuery}` : '';

  return (
    <PublicLayout>
      <Seo title={`${l.title} — Case in vendita`} description={stripHtml(l.description).slice(0, 150)} image={cover ? mediaUrl(cover) : undefined} />
      <div className="sila-section py-8">
        <Link to="/case-in-vendita" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="re-detail-back"><ArrowLeft className="h-4 w-4" />Case in vendita</Link>

        <div className="mt-4 grid gap-8 lg:grid-cols-[1.5fr_1fr]">
          {/* Colonna sinistra: galleria + dati */}
          <div>
            <div className="overflow-hidden rounded-2xl border border-border bg-muted">
              <div className="relative aspect-[16/10]">
                {cover ? <img src={mediaUrl(cover)} alt={l.title} className="h-full w-full object-cover" data-testid="re-detail-cover" /> : <div className="flex h-full items-center justify-center"><ImageIcon className="h-10 w-10 text-muted-foreground" /></div>}
              </div>
              {photos.length > 1 && (
                <div className="flex gap-2 overflow-x-auto p-2">
                  {photos.map((p, i) => (
                    <button key={p.url} onClick={() => setActive(i)} className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? 'border-primary' : 'border-transparent'}`} data-testid={`re-detail-thumb-${i}`}>
                      <img src={mediaUrl(p.url)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-6">
              <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="re-detail-title">{l.title}</h1>
              {address && (
                <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-muted-foreground">
                  <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-primary" />{address}</span>
                  {mapHref && (
                    <a href={mapHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" data-testid="re-detail-map-link">
                      Apri in Google Maps<ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </p>
              )}
              <p className="mt-3 text-2xl font-bold text-emerald-700 dark:text-emerald-400">{euro(l.price)}</p>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Spec icon={Maximize} label="Superficie" value={l.sqm != null ? `${l.sqm} m²` : ''} />
              <Spec icon={BedDouble} label="Locali" value={l.rooms} />
              <Spec icon={Bath} label="Bagni" value={l.bathrooms} />
              <Spec icon={ArrowUpDown} label="Piano" value={l.floor} />
              <Spec icon={Building2} label="Ascensore" value={l.elevator ? 'Sì' : 'No'} />
              <Spec icon={Zap} label="Classe energetica" value={l.energy_class} />
            </div>

            {l.description && stripHtml(l.description) && (
              <div className="mt-6">
                <h2 className="font-display text-lg font-semibold">Descrizione</h2>
                <div className="sila-prose mt-2 leading-relaxed" data-testid="re-detail-desc" dangerouslySetInnerHTML={{ __html: l.description }} />
              </div>
            )}
          </div>

          {/* Colonna destra: form contatti (sticky) */}
          <div>
            <Card className="sticky top-24 p-5" data-testid="re-contact-card">
              {sent ? (
                <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="re-contact-success">
                  <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                  <p className="font-medium">Richiesta inviata!</p>
                  <p className="text-sm text-muted-foreground">L’amministrazione ti ricontatterà a breve.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <h2 className="font-display text-lg font-semibold">Richiedi informazioni</h2>
                    <p className="text-sm text-muted-foreground">Scrivi all’amministrazione per questo immobile.</p>
                  </div>
                  <div className="space-y-1.5"><Label htmlFor="re-name">Nome e cognome *</Label><Input id="re-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="re-contact-name" /></div>
                  <div className="space-y-1.5"><Label htmlFor="re-email">Email *</Label><Input id="re-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="re-contact-email" /></div>
                  <div className="space-y-1.5"><Label htmlFor="re-phone">Telefono</Label><Input id="re-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="re-contact-phone" /></div>
                  <div className="space-y-1.5"><Label htmlFor="re-msg">Messaggio</Label><Textarea id="re-msg" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={`Sono interessato/a a "${l.title}"…`} data-testid="re-contact-message" /></div>
                  <Button type="submit" className="w-full" disabled={sending} data-testid="re-contact-submit">
                    {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Invia richiesta
                  </Button>
                </form>
              )}
            </Card>
          </div>
        </div>
      </div>

      <ExperiencesCrossSell />
    </PublicLayout>
  );
}
